// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { clipboardText } from "@adl/App/Widgets/DesignSystem/Utilities";
import CopyLink from "@adl/XDL/Icons/CopyLink";
import Email from "@adl/XDL/Icons/Email";
import Print from "@adl/XDL/Icons/Print";
import { xScreen } from "@adl/XDL/Utilities";
import { createLinkIconStyles } from "@adl/XDL/Utilities/sharedStyles";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductShareLink, { ProductShareLinkStyles } from "@insite/content-library/Components/ProductShareLink";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import ToasterContext, { useToaster } from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

export interface ProductDetailsShareLinkStyles {
    wrapper?: InjectableCss;
    link?: ProductShareLinkStyles;
    // XNG-Changes
    printLink?: LinkPresentationProps;
    copyLink?: LinkPresentationProps;
}

export const shareLinkStyles: ProductDetailsShareLinkStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            align-items: center;
            flex-wrap: wrap;
            margin-top: 16px;
            ${xScreen("lte", "lg")} {
                margin-bottom: 16px;
            }
        `,
    },
    link: {
        link: {
            ...createLinkIconStyles(Email),
            css: css`
                margin-right: 28px;
            `,
        },
    },
    printLink: {
        ...createLinkIconStyles(Print),
        css: css`
            margin-right: 28px;
        `,
    },
    copyLink: {
        ...createLinkIconStyles(CopyLink),
    },
};

const styles = shareLinkStyles;

const ProductDetailsShareLink: React.FC<Props> = ({ product }) => {
    // <XNG-Changes>
    const toaster = useToaster();
    // </XNG-Changes>
    if (!product) {
        return null;
    }

    // <XNG-Changes>
    const handleCopyLink = () => {
        const isCopied = clipboardText(window.location.href);
        toaster.addToast({
            body: isCopied ? "Copied !" : "Copy Failed",
            messageType: isCopied ? "success" : "danger",
        });
    };
    // </XNG-Changes>

    return (
        <StyledWrapper {...styles.wrapper}>
            <ProductShareLink text={translate("Email")} extendedStyles={styles.link} />
            {/* <XNG-Changes> */}
            <Link {...styles.printLink} onClick={openPrintDialog}>
                {translate("Print")}
            </Link>
            <Link {...styles.copyLink} onClick={handleCopyLink}>
                {translate("Copy Link")}
            </Link>
            {/* </XNG-Changes> */}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(withProduct(ProductDetailsShareLink)),
    definition: {
        displayName: "Share Link",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
